import { StorageAuth } from "./../localStorage/StorageAuth";
import { SWRResponse } from "swr";
import { ApiSchemaCaregiver } from "../../models/api";
import { AxiosAuth } from "../axios";
import {
  useExtendedApiAccess,
  CreateAccessObject,
} from "./useExtendedApiAccess";
import { AppRouteApi } from "../../config/appRoute";
import { PaginatedData } from "../../models/api";
import { useRef, useEffect } from "react";
import { ApiResponseContainer } from "../../models/api";
import { useAppDispatch } from "../redux/ReduxStore";
import { ActionsCaregiverTable } from "../redux/slices/sliceCaregiverTable";
import { showErrorToast } from "../../components/toast.component";
import { generateMultipartFormData } from "../../utils/formUtils";

const CacheIds = {
  GetAll: AppRouteApi.Caregiver.All(),
  List: AppRouteApi.Caregiver.List(),
  ById: AppRouteApi.Caregiver.ById(""),
};

function useApiCaregiverGetAll(
  companyId?: string
): SWRResponse<ApiSchemaCaregiver[] | undefined, Error> {
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const api = useExtendedApiAccess<ApiSchemaCaregiver[]>(CacheIds.GetAll, () =>
    AxiosAuth.get(CacheIds.GetAll, {
      params: {
        CompanyId: companyId ? companyId : StorageAuth.CompanyId,
      },
    })
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      (async () => {
        dispatch(ActionsCaregiverTable.setIsLoading(true));
        const res = await AxiosAuth.get<
          ApiResponseContainer<ApiSchemaCaregiver[]>
        >(CacheIds.GetAll, {
          params: {
            CompanyId: companyId ? companyId : StorageAuth.CompanyId,
          },
        });

        if (res.data.isSuccess) {
          api.mutate(res.data.data, false);
        }
        dispatch(ActionsCaregiverTable.setIsLoading(false));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return api;
}

function useApiCaregiverById(
  id: string
): SWRResponse<ApiSchemaCaregiver | undefined, Error> {
  return useExtendedApiAccess<ApiSchemaCaregiver>(CacheIds.ById, () =>
    AxiosAuth.get(AppRouteApi.Caregiver.ById(id))
  );
}

function useApiCaregiverOfCompanyGetAll(
  companyId?: string
): SWRResponse<ApiSchemaCaregiver[] | undefined, Error> {
  const api = useExtendedApiAccess<ApiSchemaCaregiver[]>(CacheIds.List, () =>
    AxiosAuth.get(CacheIds.List, {
      params: {
        CompanyId: companyId ? companyId : StorageAuth.CompanyId,
      },
    })
  );

  return api;
}

const AccessObject = {
  GetAll: CreateAccessObject<
    PaginatedData<ApiSchemaCaregiver>,
    typeof useApiCaregiverGetAll
  >(CacheIds.GetAll, useApiCaregiverGetAll),

  ById: CreateAccessObject<ApiSchemaCaregiver, typeof useApiCaregiverById>(
    CacheIds.ById,
    useApiCaregiverById
  ),

  GetAllFromCompany: CreateAccessObject<
    PaginatedData<ApiSchemaCaregiver>,
    typeof useApiCaregiverOfCompanyGetAll
  >(CacheIds.List, useApiCaregiverOfCompanyGetAll),
};

async function AddCaregiver(
  Caregiver: FormData
): Promise<ApiResponseContainer<ApiSchemaCaregiver>> {
  try {
    const response = await AxiosAuth.post<
      ApiResponseContainer<ApiSchemaCaregiver>
    >(AppRouteApi.Caregiver.Root(), Caregiver);
    if (response.data.isSuccess) {
      const updAction = (
        res?: PaginatedData<ApiSchemaCaregiver>
      ): PaginatedData<ApiSchemaCaregiver> | undefined => {
        if (res && Array.isArray(res?.list) && response.data.data) {
          return {
            totalCount: res.totalCount + 1,
            list: [...res.list, response.data.data],
          };
        } else if (res) return res;
      };
      AccessObject.GetAll.Mutate(updAction());
    }
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

async function UpdateCaregiver(
  Caregiver: FormData
): Promise<ApiResponseContainer<ApiSchemaCaregiver>> {
  try {
    const response = await AxiosAuth.put<
      ApiResponseContainer<ApiSchemaCaregiver>
    >(AppRouteApi.Caregiver.Root(), Caregiver);
    if (response.data.isSuccess) {
      const updAction = (
        res?: PaginatedData<ApiSchemaCaregiver>
      ): PaginatedData<ApiSchemaCaregiver> | undefined => {
        if (res && Array.isArray(res?.list) && response.data.data) {
          return {
            totalCount: res.totalCount + 1,
            list: [...res.list, response.data.data],
          };
        } else if (res) return res;
      };
      AccessObject.GetAll.Mutate(updAction());
    }
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

async function DeleteCaregiver(ids: string[]): Promise<boolean> {
  try {
    const response = await AxiosAuth.delete<ApiResponseContainer<boolean>>(
      AppRouteApi.Caregiver.Delete(ids)
    );
    if (response.data.isSuccess) return true;
  } catch (e: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return false;
}

function generateCaregiverBody(caregiver: ApiSchemaCaregiver) {
  const body = {
    SKey: caregiver.sKey,
    Email: caregiver.email,
    FirstName: caregiver.firstName,
    LastName: caregiver.lastName,
    PhoneNumber: caregiver.phoneNumber,
    CompanyId: caregiver.companyId,
    Gender: caregiver.gender,
    Address: caregiver.address,
    DateOfBirth: caregiver.dateOfBirth,
    UserName: caregiver.userName,
    Rate: `${caregiver.rate}`,
    Ssn: caregiver.ssn,
    ChampsIdNo: caregiver.champsIdNo,
    Languages: caregiver.languages,
    IsTransportAvailable: `${caregiver.isTransportAvailable}`,
    AvailableFrom: caregiver.availableFrom,
    AvailableTo: caregiver.availableTo,
    WeeklyAvailabilities: caregiver.weeklyAvailabilities,
    AutoClockCheckIn: `${caregiver.autoClockCheckIn}`,
    GpsCheckInTurnedOn: `${caregiver.gpsCheckInTurnedOn}`,
    IsNotificationEnabled: `${caregiver.isNotificationEnabled}`,
    StartDate: caregiver.startDate,
    EndDate: caregiver.endDate,
  };
  return body;
}

async function ToggleAutoClockIn(
  caregiver: ApiSchemaCaregiver,
  autoClock: boolean,
  startDate?: string | Date,
  endDate?: string | Date
) {
  const body = generateCaregiverBody(caregiver);
  body.AutoClockCheckIn = `${autoClock}`;
  if (autoClock) {
    body.StartDate = startDate as string;
    body.EndDate = endDate as string;
  }
  const formData = generateMultipartFormData(body);
  return UpdateCaregiver(formData);
}

async function ToggleGPSCheckIn(
  caregiver: ApiSchemaCaregiver,
  GpsCheckInTurnedOn: boolean
) {
  const body = generateCaregiverBody(caregiver);
  body.GpsCheckInTurnedOn = `${GpsCheckInTurnedOn}`;
  const formData = generateMultipartFormData(body);
  return UpdateCaregiver(formData);
}

async function ToggleNotification(
  caregiver: ApiSchemaCaregiver,
  isNotificationEnabled: boolean
) {
  const body = generateCaregiverBody(caregiver);
  body.IsNotificationEnabled = `${isNotificationEnabled}`;
  const formData = generateMultipartFormData(body);
  return UpdateCaregiver(formData);
}

async function ActivateDeactivateCaregiver(
  id: string,
  deactivate: boolean
): Promise<ApiResponseContainer<ApiSchemaCaregiver>> {
  try {
    const response = await AxiosAuth.put<
      ApiResponseContainer<ApiSchemaCaregiver>
    >(
      AppRouteApi.Caregiver.ActivateDeactivate(id),
      {},
      {
        params: {
          deactivate: deactivate,
        },
      }
    );
    if (response.data.isSuccess) {
      const updAction = (
        res?: PaginatedData<ApiSchemaCaregiver>
      ): PaginatedData<ApiSchemaCaregiver> | undefined => {
        if (res && Array.isArray(res?.list) && response.data.data) {
          return {
            totalCount: res.totalCount + 1,
            list: [...res.list, response.data.data],
          };
        } else if (res) return res;
      };
      AccessObject.GetAll.Mutate(updAction());
    }
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

export const ApiCaregiver = {
  GetAll: AccessObject.GetAll,
  ById: AccessObject.ById,
  GetAllFromCompany: AccessObject.GetAllFromCompany,
  Add: AddCaregiver,
  Delete: DeleteCaregiver,
  Update: UpdateCaregiver,
  ToggleAutoClockIn,
  ToggleGPSCheckIn,
  ToggleNotification,
  ActivateDeactivate: ActivateDeactivateCaregiver,
};
